import _ from 'lodash';
import PropTypes from 'prop-types';
import React, {useEffect, useRef, useState} from 'react';
import NumberFormat from 'react-number-format';
import postal from 'postal';
import {setCaretPosition} from '../util';

function areValuesSame(v1Param:number|null|undefined, v2Param:number|null|undefined) {
  let v1 = v1Param;
  let v2 = v2Param;

  if (v1Param === null || v1Param === undefined || isNaN(v1Param)) {
    v1 = null;
  }

  if (v2Param === null || v2Param === undefined || isNaN(v2Param)) {
    v2 = null;
  }

  return v1 === v2;
}

function setCaretPositionBeforeDot(elemId:string) {
  const elem = document.getElementById(elemId) as HTMLInputElement;
  const value = elem.value;
  let caretPos = value ? value.indexOf('.') : 0;

  if (caretPos < 0 && value === '0') {
    caretPos = 1; // If there are no decimal places and there is leading 0
  }

  if ( caretPos < 0) {
    return;
  }

  setCaretPosition(elem, caretPos);

  setTimeout(() => {
    setCaretPosition(elem, caretPos);
  }, 0);
}

interface ExternalValueChangeRequest {
  external: boolean,
  value: number|null,
}

export interface FloatProps {
  value:number|null|undefined,
  onChange: (x:number|null) => void,
  onFocusOut?: (x:number|null) => void,
  disabled?: boolean,
  onEnter?: (() => void)|undefined,
  min?: number|undefined,
  max?: number|undefined,
  floatId?: string|undefined,
  error?: boolean,
  red?: boolean,
  decimals?: number,
  backgroundColor?: string|undefined,
  noborder?: boolean,
  onTabNext?: (() => boolean)|undefined,
  onTabPrevious?: (() => boolean)|undefined,
  style?: React.CSSProperties|undefined,
  alignLeft?: boolean,
  plain?: boolean,
  allowLeadingZeros?: boolean,
  name?:string|undefined,
  placeholder?: string|null|undefined,
  thousandSeparator?: boolean,
};

function Float({
  value,
  onChange = () => {},
  onFocusOut = () => {},
  onEnter = undefined,
  disabled = false,
  min = undefined,
  max = undefined,
  floatId = undefined,
  error = false,
  red = false,
  decimals = 2,
  onTabNext = undefined,
  onTabPrevious = undefined,
  backgroundColor = undefined,
  noborder = false,
  style = undefined,
  alignLeft = false,
  plain = false,
  allowLeadingZeros = false,
  name=undefined,
  placeholder = undefined,
  thousandSeparator = true,
  ...other
}:FloatProps) {
  const dark = false;
  let inputId = useRef<string>(floatId ?? '');
  
  const [externalValueChangeRequest, setExternalValueChangeRequest] = useState<ExternalValueChangeRequest>({external: false, value: null});
  const [fixedDecimals, setFixedDecimals] = useState<boolean>(true);
  const valueRemovedManually = useRef(false);

  let channelSubscription:ISubscriptionDefinition<any>|null = null;

  function subscribeChannels() {
    channelSubscription = postal.subscribe({
      channel: 'ifms',
      topic: 'ifms.set-number',
      callback: onValueChangeRequestByParent,
    });
  }

  function unsubscribeChannels() {
    if (channelSubscription) {
      postal.unsubscribe(channelSubscription);
    }
  }

  useEffect(() => {
    // Implementation note:
    //   The idea here is to not call handlers directly in postal callback, but to set state to avoid calling old version of react event handler (problems with references)
    if (!externalValueChangeRequest.external) {
      return;
    }

    doHandleChange(externalValueChangeRequest.value);
    setTimeout(() => setCaretPositionBeforeDot(inputId.current), 5);
    
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalValueChangeRequest]);

  useEffect(() => {
    subscribeChannels();

    if (!inputId.current) {
      inputId.current = _.uniqueId('ifms-float-');
    }

    return () => {
      unsubscribeChannels();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onValueChangeRequestByParent({parentId, number}:{parentId:string, number:number|null}) {
    const current = document.getElementById(inputId.current);
    
    if (current?.parentElement?.id === parentId) {
      setExternalValueChangeRequest({external: true, value: number});
    }
  }

  function processTabNext(e:React.KeyboardEvent<HTMLInputElement>) {
    if (onTabNext) {
      const tabProcessed = onTabNext();
      if (tabProcessed) {
        e.stopPropagation();
        e.preventDefault();
        return;
      }
    }
  }

  function processTabPrevious(e:React.KeyboardEvent<HTMLInputElement>) {
    if (onTabPrevious) {
      const tabProcessed = onTabPrevious();
      if (tabProcessed) {
        e.stopPropagation();
        e.preventDefault();
        return;
      }
    }
  }

  function onKeyDown(e:React.KeyboardEvent<HTMLInputElement>) {
    const up = 38;
    const down = 40;
    const backspace = 8;

    if (e.keyCode === backspace) {
      const currentValue = (e.target as HTMLInputElement).value ?? '';
      const dotIndex = currentValue.indexOf('.');
      const valueBeforeDot = dotIndex >= 0 ? currentValue.substring(0, dotIndex) : currentValue;

      if (valueBeforeDot.length === 1) {
        // Last digit will be removed. Move caret position into the right place
        setTimeout(() => setCaretPositionBeforeDot(inputId.current), 5);
      }
    }

    if (e.keyCode === up || e.keyCode === down) {
    //   const parentId = (e?.target as Element|null)?.parentElement?.id;
    //   if (!!parentId) {
    //     channel.publish('ifms.control-navigation', {
    //       parentId,
    //       keyCode: e.keyCode
    //     });
    //   }
    //   e.preventDefault();
    //   e.stopPropagation();
    }

    const tab = 9;

    if (e.keyCode === tab) {
      if (e.shiftKey) {
        processTabPrevious(e);
      } else {
        processTabNext(e);
      }
    }
  }

  function onKeyUp(e:React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      if (onEnter) {
        onEnter();
      }
    }
  }

  function handleChange(n:any) {
    doHandleChange(n.floatValue);
  }

  function doHandleChange(floatValue:any) {
    if (min !== null && min !== undefined && floatValue !== null && floatValue !== undefined) {
      if (floatValue < min) {
        return;
      }
    }
    if (max !== null && max !== undefined && floatValue !== null && floatValue !== undefined) {
      if (floatValue > max) {
        return;
      }
    }

    if (!areValuesSame(value, floatValue)) {
      const newVal = floatValue !== undefined ? floatValue : null; // Small changed to be able to use change event with lodash merge (which ignores undefined values)
      valueRemovedManually.current = newVal === null;
      onChange(newVal); 
    }
  }

  function onClick(e:React.MouseEvent) {
    if (!disabled) {
      (e.target  as HTMLInputElement).select();
    }
  }

  function handleFocus() {
    valueRemovedManually.current = false;
    setFixedDecimals(false);
  }

  function handleBlur(e: React.FocusEvent<HTMLInputElement>) {
    valueRemovedManually.current = false;
    setFixedDecimals(true);
    const value = parseFloat(e.target.value.replaceAll(',',''));

    if (isNaN(value)) {
      doHandleChange(0);
    }

    if (onFocusOut.length > 0) {
      onFocusOut(value);
    }  
  }

  let cssClass = dark ? `${!alignLeft ? 'e-ifms-float' : ''} e-ifms-input-dark e-outline ifms-tabable` : `${!alignLeft ? 'e-ifms-float' : ''} e-ifms-input e-outline ifms-tabable`;

  if (error) {
    cssClass = `${cssClass} e-ifms-float-error`;
  }
  if (red) {
    cssClass = `${cssClass} e-ifms-float-red`;
  }
  if (noborder) {
    cssClass = `${cssClass} ifms-noborder-input`;
  }
  if (plain) {
    cssClass = `${cssClass} ifms-plain-input`;
  }
  if (!disabled) {
    cssClass = `${cssClass} ifms-tabable-active`;
  }

  const componentValue = !valueRemovedManually.current && value !== null && value !== undefined ? value : '';

  let appliedStyle:React.CSSProperties|undefined = style;
  if (!!backgroundColor && !plain) {
    appliedStyle= appliedStyle ? { ...appliedStyle, backgroundColor } : { backgroundColor };
  }

  return (
    <NumberFormat
      id={inputId.current}
      name={name}
      placeholder={placeholder ?? undefined}
      style={appliedStyle}
      allowLeadingZeros={true}
      thousandSeparator={thousandSeparator}
      className={cssClass}
      decimalScale={decimals}
      fixedDecimalScale={fixedDecimals}
      value={componentValue}
      onValueChange={handleChange}
      disabled={disabled} 
      onKeyDown={onKeyDown}
      onKeyUp={onKeyUp}
      autoComplete="off"
      list="autocompleteOff"
      data-tooltip="-from-value"
      
      onClick={onClick}
      onFocus={handleFocus}
      onBlur={handleBlur}
      data-tooltip-onlywhencutoff
        isAllowed={values => {
          if (!values.value) {
            return true;
          }

          const minCheck = min === null || min === undefined || (values.floatValue ?? 0) >= min;
          const maxCheck = max === null || max === undefined || (values.floatValue ?? 0) <= max;

          return minCheck && maxCheck;
        }}
        {..._.omit(other, ['dark'])}
    />
  );
}

Float.defaultProps = {
  onChange: () => {},
  onFocusOut: () => {},
  disabled: false,
  dark: false,
  noborder: false,
  plain: false,
  alignLeft: false,
  allowLeadingZeros: false,
  thousandSeparator :true,  
  placeholder: '',
};

Float.propTypes = {
  onChange: PropTypes.func.isRequired,
  onEnter: PropTypes.func,
  value: PropTypes.number,
  min: PropTypes.number,
  max: PropTypes.number,
  dark: PropTypes.bool.isRequired,
  disabled: PropTypes.bool.isRequired,
  floatId: PropTypes.string,
  noborder: PropTypes.bool.isRequired,
  plain: PropTypes.bool.isRequired,
  alignLeft: PropTypes.bool,
  allowLeadingZeros: PropTypes.bool,  
  thousandSeparator : PropTypes.bool,  
  placeholder: PropTypes.string,
};


export default React.memo(Float);
